import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Check, Copy, Download, Key, ShieldCheck, Eye, EyeOff } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useOwner } from './OwnerContext';
import { ownerError } from './ownerAPI';

export default function OwnerLogin() {
  const { user, login } = useOwner();
  const navigate = useNavigate();

  // step: 'credentials' | 'mfa' | 'setup' | 'recovery'
  const [step, setStep] = useState('credentials');
  const [form, setForm] = useState({ username: '', password: '', code: '' });
  const [setupData, setSetupData] = useState(null);
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [savedRecovery, setSavedRecovery] = useState(false);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [show, setShow] = useState(false);

  if (user && step !== 'recovery') return <Navigate to="/owner/overview" replace />;

  const submit = async event => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const payload = {
        username: form.username.trim(),
        password: form.password,
        code: form.code ? form.code.trim() : '',
      };
      const result = await login(payload);

      if (result?.mfa_required) {
        setStep('mfa');
        setForm(f => ({ ...f, code: '' }));
      } else if (result?.mfa_setup_required) {
        setSetupData(result);
        setStep('setup');
        setForm(f => ({ ...f, code: '' }));
      } else if (result?.recovery_codes?.length) {
        setRecoveryCodes(result.recovery_codes);
        setStep('recovery');
      } else {
        navigate('/owner/overview', { replace: true });
      }
    } catch (failure) {
      setError(ownerError(failure));
    } finally {
      setBusy(false);
    }
  };

  const copyCodes = async () => {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {}
  };

  const downloadCodes = () => {
    const text = `WEBTRA Platform Owner Emergency Recovery Codes\nUser: ${form.username}\nDate: ${new Date().toISOString()}\n\n${recoveryCodes.join('\n')}\n`;
    const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
    const a = Object.assign(document.createElement('a'), { href: url, download: 'owner-recovery-codes.txt' });
    a.click();
    URL.revokeObjectURL(url);
    setSavedRecovery(true);
  };

  const restart = () => {
    setStep('credentials');
    setForm({ username: '', password: '', code: '' });
    setSetupData(null);
    setError('');
  };

  return (
    <main className="owner-login" data-testid="owner-login-page">
      <div className="owner-login-top">
        <span className="owner-wordmark" data-testid="owner-login-wordmark">
          BROKERAGE<span> / OWNER</span>
        </span>
        <span data-testid="owner-login-private">
          <ShieldCheck size={14} />Private access
        </span>
      </div>

      <div className="owner-login-body">
        <div className="owner-login-heading">
          <span className="owner-eyebrow" data-testid="owner-login-eyebrow">
            {step === 'credentials' && 'PLATFORM ADMINISTRATION'}
            {step === 'mfa' && 'SECURITY VERIFICATION'}
            {step === 'setup' && 'FIRST-TIME CONNECTION'}
            {step === 'recovery' && 'EMERGENCY RECOVERY'}
          </span>
          <h1>
            {step === 'credentials' && 'Owner sign in'}
            {step === 'mfa' && 'Two-factor code'}
            {step === 'setup' && 'Set up 2FA'}
            {step === 'recovery' && 'Save recovery codes'}
          </h1>
          <p>
            {step === 'credentials' && 'Manage your white-label brokerages.'}
            {step === 'mfa' && 'Enter the 6-digit code from your authenticator app.'}
            {step === 'setup' && 'Scan the QR code with Google Authenticator or Authy.'}
            {step === 'recovery' && 'These codes can be used to sign in if you lose your device.'}
          </p>
        </div>

        {/* STEP 1: USERNAME + PASSWORD */}
        {step === 'credentials' && (
          <form onSubmit={submit} data-testid="owner-login-form">
            <label htmlFor="owner-login-username">
              Username
              <Input
                id="owner-login-username"
                data-testid="owner-login-username"
                autoComplete="username"
                required
                autoFocus
                value={form.username}
                onChange={event => setForm({ ...form, username: event.target.value })}
              />
            </label>

            <label htmlFor="owner-login-password">
              Password
              <div className="owner-password-input">
                <Input
                  id="owner-login-password"
                  data-testid="owner-login-password"
                  type={show ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={form.password}
                  onChange={event => setForm({ ...form, password: event.target.value })}
                />
                <button
                  type="button"
                  data-testid="owner-login-toggle-password"
                  aria-label={show ? 'Hide password' : 'Show password'}
                  onClick={() => setShow(!show)}
                >
                  {show ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
            </label>

            {error && (
              <p role="alert" className="owner-error" data-testid="owner-login-error">
                {error}
              </p>
            )}

            <Button
              type="submit"
              className="owner-primary"
              disabled={busy}
              data-testid="owner-login-submit"
            >
              {busy ? 'Signing in…' : 'Sign in'}
              <ArrowRight size={16} />
            </Button>
          </form>
        )}

        {/* STEP 2A: 2FA CODE */}
        {step === 'mfa' && (
          <form onSubmit={submit} data-testid="owner-login-mfa-form">
            <label htmlFor="owner-login-code">
              Authenticator or recovery code
              <Input
                id="owner-login-code"
                data-testid="owner-login-code"
                autoComplete="one-time-code"
                maxLength={32}
                autoFocus
                placeholder="123456"
                value={form.code}
                onChange={event => setForm({ ...form, code: event.target.value.trim() })}
              />
            </label>

            {error && (
              <p role="alert" className="owner-error" data-testid="owner-login-error">
                {error}
              </p>
            )}

            <Button
              type="submit"
              className="owner-primary"
              disabled={busy || !form.code}
              data-testid="owner-login-verify"
            >
              {busy ? 'Verifying…' : 'Verify & Continue'}
              <ArrowRight size={16} />
            </Button>

            <button
              type="button"
              onClick={restart}
              style={{ background: 'none', border: 0, color: 'var(--ow-muted)', cursor: 'pointer', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '6px', margin: '14px auto 0' }}
            >
              <ArrowLeft size={13} /> Sign in as different user
            </button>
          </form>
        )}

        {/* STEP 2B: FIRST TIME SETUP (QR CODE) */}
        {step === 'setup' && setupData && (
          <form onSubmit={submit} data-testid="owner-login-setup-form">
            {setupData.qr && (
              <div style={{ textAlign: 'center', margin: '0 0 16px' }}>
                <img
                  src={setupData.qr}
                  alt="2FA QR code"
                  style={{ width: '180px', height: '180px', padding: '10px', background: '#fff', border: '1px solid #dce5df', borderRadius: '8px', display: 'inline-block' }}
                />
              </div>
            )}

            <details style={{ fontSize: '11px', color: 'var(--ow-muted)', marginBottom: '14px' }}>
              <summary style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <Key size={13} /> Can't scan? Enter key manually
              </summary>
              <code style={{ display: 'block', padding: '10px', background: '#f0f5f1', border: '1px solid #dce5df', borderRadius: '4px', margin: '8px 0', fontFamily: 'monospace', fontSize: '12px', userSelect: 'all' }}>
                {setupData.secret}
              </code>
            </details>

            <label htmlFor="owner-setup-code">
              6-digit verification code from app
              <Input
                id="owner-setup-code"
                data-testid="owner-setup-code"
                required
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                autoFocus
                placeholder="123456"
                value={form.code}
                onChange={event => setForm({ ...form, code: event.target.value.replace(/\D/g, '') })}
              />
            </label>

            {error && (
              <p role="alert" className="owner-error" data-testid="owner-login-error">
                {error}
              </p>
            )}

            <Button
              type="submit"
              className="owner-primary"
              disabled={busy || form.code.length !== 6}
              data-testid="owner-setup-submit"
            >
              {busy ? 'Activating…' : 'Activate & Continue'}
              <ArrowRight size={16} />
            </Button>

            <button
              type="button"
              onClick={restart}
              style={{ background: 'none', border: 0, color: 'var(--ow-muted)', cursor: 'pointer', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '6px', margin: '14px auto 0' }}
            >
              <ArrowLeft size={13} /> Cancel and go back
            </button>
          </form>
        )}

        {/* STEP 2C: RECOVERY CODES */}
        {step === 'recovery' && (
          <div data-testid="owner-recovery-screen">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 12px', padding: '12px', background: '#f0f5f1', border: '1px dashed #b9d3c1', borderRadius: '6px', margin: '0 0 16px', fontFamily: 'monospace', fontSize: '12px' }}>
              {recoveryCodes.map(code => (
                <div key={code} style={{ userSelect: 'all' }}>{code}</div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '10px', marginBottom: '18px' }}>
              <Button type="button" variant="outline" style={{ flex: 1, fontSize: '11px', display: 'flex', gap: '6px', justifyContent: 'center' }} onClick={copyCodes}>
                {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? 'Copied' : 'Copy'}
              </Button>
              <Button type="button" variant="outline" style={{ flex: 1, fontSize: '11px', display: 'flex', gap: '6px', justifyContent: 'center' }} onClick={downloadCodes}>
                <Download size={14} /> Download
              </Button>
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', marginBottom: '20px' }}>
              <input
                type="checkbox"
                checked={savedRecovery}
                onChange={e => setSavedRecovery(e.target.checked)}
              />
              I have securely saved these emergency codes
            </label>

            <Button
              type="button"
              className="owner-primary"
              disabled={!savedRecovery}
              onClick={() => navigate('/owner/overview', { replace: true })}
            >
              Go to Overview
              <ArrowRight size={16} />
            </Button>
          </div>
        )}
      </div>

      <footer data-testid="owner-login-footer">
        Brokerage-level administration · Restricted access
      </footer>
    </main>
  );
}